/**
 * Every file ever received, in one place.
 *
 * Pure by design: the listing must hold in a test without the DOM, so the
 * shaping lives here and the surface in `shell.ts` only draws the result.
 */
import type { StoredMail } from "../store/db";
import type { BlobRef } from "../nostr/seal";

export interface FileRow {
  file: BlobRef;
  /** The message the file arrived on, for the row's sender and date. */
  origin: StoredMail;
  /** Sender address. The row searches on this and on any known name. */
  sender: string;
}

/**
 * Every non-inline attachment across all mail, newest message first.
 *
 * Inline images are placed by the body they belong to and are not files to
 * save, which is the same line the reader's attachment list already takes.
 * A message this account sent is not received mail, so it holds no files to
 * list here.
 */
export function collectFiles(
  held: readonly StoredMail[],
  query = "",
  names: Readonly<Record<string, string>> = {},
): FileRow[] {
  const needle = query.trim().toLowerCase();
  const rows: FileRow[] = [];
  for (const item of held) {
    if (item.sent) continue;
    const files = (item.attachments ?? []).filter((a) => !a.inline);
    if (files.length === 0) continue;
    const sender = item.senderAddress || item.from;
    for (const file of files) {
      rows.push({ file, origin: item, sender });
    }
  }
  if (!needle) return rows;
  return rows.filter(({ sender, origin, file }) => {
    const haystack = [
      file.filename, origin.subject ?? "", sender, names[sender] ?? "",
    ].join(" ").toLowerCase();
    return haystack.includes(needle);
  });
}
